import { useEffect, useState, useCallback } from "react";
import { branchesAPI } from "../services/api";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import toast from "react-hot-toast";

const blank = { name:"", address:"", city:"Accra", lat:"", lng:"", manager_id:"" };

export default function Branches() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(null);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(blank);
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await branchesAPI.list(); setBranches(r.data.data || []); }
    catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(blank); setEditing(null); setModal("create"); };
  const openEdit   = (b) => { setForm({...b}); setEditing(b); setModal("edit"); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal === "create") { await branchesAPI.create(form); toast.success("Branch created"); }
      else { await branchesAPI.update(editing.id, form); toast.success("Branch updated"); }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.message||"Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Deactivate "${b.name}"?`)) return;
    try { await branchesAPI.delete(b.id); toast.success("Branch deactivated"); load(); }
    catch { toast.error("Failed"); }
  };

  const F = ({ label, field, type="text", placeholder="" }) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input className="input" type={type} placeholder={placeholder} value={form[field]||""} onChange={e=>setForm(f=>({...f,[field]:e.target.value}))} />
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Branches</div>
          <div className="page-subtitle">{branches.length} active branches</div>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={15}/> New Branch</button>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:16}}>
        {loading
          ? <div className="loading-center"><div className="spinner"/></div>
          : branches.length === 0
            ? <div className="empty-state" style={{gridColumn:"1/-1"}}><MapPin size={40}/><p>No branches yet</p></div>
            : branches.map(b => (
          <div key={b.id} className="card">
            <div className="card-body">
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <div className="fw-bold" style={{fontSize:15,marginBottom:4}}>{b.name}</div>
                  <div style={{fontSize:12,color:"var(--text2)",marginBottom:8}}>{b.address}</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <span className="badge badge-active">{b.city}</span>
                    {b.status && <span className={`badge ${b.status==="active"?"badge-active":"badge-suspended"}`}>{b.status}</span>}
                  </div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>openEdit(b)}><Pencil size={13}/></button>
                  <button className="btn btn-danger btn-icon btn-sm" onClick={()=>handleDelete(b)}><Trash2 size={13}/></button>
                </div>
              </div>
              {b.manager_name && (
                <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)",fontSize:12,color:"var(--text3)"}}>
                  Manager: <span style={{color:"var(--text)"}}>{b.manager_name}</span>
                </div>
              )}
              {b.lat && (
                <div className="mono" style={{fontSize:11,color:"var(--text3)",marginTop:6}}>
                  {parseFloat(b.lat).toFixed(4)}, {parseFloat(b.lng).toFixed(4)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{modal==="create"?"New Branch":"Edit Branch"}</div>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <F label="Branch Name" field="name" placeholder="e.g. Quickie – Osu" />
              <F label="Address" field="address" placeholder="Street address" />
              <div className="form-row">
                <F label="City" field="city" placeholder="Accra" />
                <F label="Manager User ID" field="manager_id" placeholder="User ID" />
              </div>
              <div className="form-row">
                <F label="Latitude" field="lat" placeholder="5.6302" />
                <F label="Longitude" field="lng" placeholder="-0.1734" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving?"Saving...":modal==="create"?"Create Branch":"Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
